import React, { useState } from 'react'
import { Button, Form as BootstrapForm, Col, Row } from 'react-bootstrap'
import { useRouter } from '@tanstack/react-router'
import * as yup from 'yup'
import { Formik } from 'formik'
import Icon from '@mdi/react'
import { mdiMagnify, mdiCheckboxMarkedCircle, mdiTimerSand, mdiAlertBoxOutline } from '@mdi/js'
import { api } from '../shared/ApiProvider'

export default function SurveyUpdateModalPermField({ id, screenerName }) {
  const router = useRouter()
  const [searchState, setSearchState] = useState('idle') // idle, searching, found, not-found, error
  const [foundStudent, setFoundStudent] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')

  const searchByPermNumber = async (data, { setSubmitting }) => {
    const permNumber = data.permNumber
    setSearchState('searching')
    setFoundStudent(null)
    setErrorMessage('')

    try {
      // Search for student by PERM number - matching the backend StudentSearchParams pattern
      const result = await api.post('/student/lookup?byPermnumber', { permNumber: permNumber })

      if (result.data && result.data.permNumber) {
        setFoundStudent(result.data)
        setSearchState('found')
      } else {
        setSearchState('not-found')
        setErrorMessage('No student found with this PERM number')
      }
    } catch (error) {
      console.error('Error searching for PERM number:', error)
      console.error('Error response data:', error.response?.data)
      console.error('Error response status:', error.response?.status)
      if (error.response?.status === 404) {
        setSearchState('not-found')
        setErrorMessage('No student found with this PERM number')
      } else if (error.response?.status === 400) {
        setSearchState('error')
        setErrorMessage(`Bad request: ${error.response?.data?.message || 'Invalid parameters'}`)
      } else {
        setSearchState('error')
        setErrorMessage('An error occurred during search. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const acceptPermAndNavigate = async () => {
    if (!foundStudent || !foundStudent.permNumber) return

    try {
      // Update the survey with the PERM number
      const result = await api.put(`/survey/${id}`, {
        permnumber: foundStudent.permNumber,
      })

      if (result.status === 200 || result.status === 303) {
        // Navigate to UpdateStudent page
        router.navigate({ to: `/survey/${id}` })
      }
    } catch (error) {
      console.error('Error accepting PERM number:', error)
      setSearchState('error')
      setErrorMessage('Error accepting PERM number. Please try again.')
    }
  }

  const schema = yup.object().shape({
    permNumber: yup.string().matches(/^\d+$/, 'Please enter a valid PERM Number').required('*required'),
  })

  return (
    <div>
      <Formik validationSchema={schema} onSubmit={searchByPermNumber} initialValues={{ permNumber: '' }}>
        {({ handleSubmit, handleChange, values, errors, touched, isSubmitting }) => (
          <BootstrapForm noValidate onSubmit={handleSubmit}>
            <Row>
              <BootstrapForm.Group as={Col} md="12" controlId="permNumber">
                <h5 className="mt-4">Or search by PERM Number</h5>
                <BootstrapForm.Label>PERM Number</BootstrapForm.Label>
                <BootstrapForm.Control
                  type="text"
                  name="permNumber"
                  value={values.permNumber}
                  onChange={handleChange}
                  isInvalid={touched.permNumber && !!errors.permNumber}
                  disabled={isSubmitting}
                />
                <BootstrapForm.Control.Feedback type="invalid">{errors.permNumber}</BootstrapForm.Control.Feedback>
              </BootstrapForm.Group>
            </Row>
            <Button type="submit" className="mt-3" disabled={isSubmitting}>
              <Icon path={mdiMagnify} className="me-2" color="white" size={0.8} />
              {isSubmitting ? 'Searching...' : 'Search PERM Number'}
            </Button>
          </BootstrapForm>
        )}
      </Formik>

      {/* Search Results */}
      {searchState === 'searching' && (
        <div className="mt-3 p-3 border rounded bg-light d-flex align-items-center">
          <Icon path={mdiTimerSand} spin size={1.5} color="blue" />
          <span className="ms-3">Searching for student...</span>
        </div>
      )}

      {searchState === 'found' && foundStudent && (
        <div className="mt-3 p-3 border rounded bg-light">
          <h6 className="text-success mb-3 d-flex align-items-center">
            <Icon path={mdiCheckboxMarkedCircle} size={1} color="green" className="me-2" />
            Student Found in Master Database
          </h6>
          <div>
            <strong>PERM Number:</strong> <span className="text-monospace">{foundStudent.permNumber}</span>
            <br />
            <strong>Name:</strong> {foundStudent.firstName} {foundStudent.middleName || ''} {foundStudent.lastName}{' '}
            {foundStudent.postName || ''}
            <br />
            {foundStudent.formerLastName && (
              <>
                <strong>Former Last Name:</strong> {foundStudent.formerLastName}
                <br />
              </>
            )}
            <strong>Date of Birth:</strong> {foundStudent.dateOfBirth}
            <br />
            <strong>Gender:</strong> {foundStudent.gender}
          </div>
          <div className="d-flex justify-content-end mt-3">
            <Button variant="success" onClick={acceptPermAndNavigate}>
              <Icon path={mdiCheckboxMarkedCircle} className="me-2" color="white" size={0.8} />
              Accept PERM & Go to Update Student
            </Button>
          </div>
        </div>
      )}

      {(searchState === 'not-found' || searchState === 'error') && (
        <div className="mt-3 p-3 border rounded bg-light">
          <div className="d-flex align-items-center text-danger">
            <Icon path={mdiAlertBoxOutline} size={1} color="red" className="me-2" />
            <strong>{errorMessage}</strong>
          </div>
        </div>
      )}
    </div>
  )
}
